import { useQuery } from '@tanstack/react-query';
import { Gavel } from 'lucide-react';
import { useState } from 'react';

import { ConfirmDialog } from '../../../components/confirm-dialog';
import { Button } from '../../../components/ui';
import { t } from '../../../lib/i18n';
import { toast } from '../../../lib/toast';
import { fetchMinimumBid } from '../api';
import { MINIMUM_BID_QUERY_KEY, useSetMinimumBid } from '../hooks/use-bid-mutations';
import { pluralUk } from '../../finance/utils';
import { formatChf } from '../utils';

/** Key suffixes of `admin.minimumBid.success.withOutbids.*` for {@link pluralUk}. */
const PLURAL_FORMS = { one: 'one', few: 'few', many: 'many' } as const;

/**
 * Per-lot minimum-bid floor control — the monorepo home of the web's
 * `MinimumBidPriceControl`, which the consolidation left with a live mutation
 * and a live route but no UI calling either.
 *
 * On a bid-relay desk this is the operator's price lever: it mirrors the REAL
 * auction's current price onto our lot so clients cannot keep bidding under
 * it. Raising the floor demotes every active bid below it to `Outbid`, so the
 * change goes through a confirmation and is audited server-side
 * (`admin.minimum_bid_set`).
 */
export function MinimumBidControl({ carId }: { carId: string }) {
  const [draft, setDraft] = useState('');
  const [confirming, setConfirming] = useState(false);
  const mutation = useSetMinimumBid();

  const stateQuery = useQuery({
    queryKey: [...MINIMUM_BID_QUERY_KEY, carId],
    queryFn: () => fetchMinimumBid(carId),
    enabled: Boolean(carId),
  });

  const current = stateQuery.data;
  const parsed = Number(draft);
  const draftIsValid = draft.trim() !== '' && Number.isFinite(parsed) && parsed >= 0;

  const requestChange = () => {
    if (!draftIsValid) {
      toast.error(t('admin.minimumBid.errors.invalidPrice'));
      return;
    }
    if (current && parsed === current.adminSetMinimum) {
      toast.error(t('admin.minimumBid.errors.samePrice'));
      return;
    }
    setConfirming(true);
  };

  const applyChange = async () => {
    if (!draftIsValid || mutation.isPending) return;
    try {
      const result = await mutation.mutateAsync({ carId, minimumBidPrice: parsed });
      const price = formatChf(result.minimumBidPrice);
      const count = result.outbidBidsCount;
      // BUG-43: «1 ставку / 3 ставки / 5 ставок», never one form for every count.
      toast.success(
        count > 0
          ? t(
              `admin.minimumBid.success.withOutbids.${pluralUk(count, PLURAL_FORMS)}`,
              { price, count },
            )
          : t('admin.minimumBid.success.updated', { price }),
      );
      setDraft('');
      await stateQuery.refetch();
    } catch {
      toast.error(t('admin.minimumBid.errors.updateFailed'));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-4 lg:px-[22px] lg:py-5">
      <div className="flex items-center gap-2">
        <Gavel size={16} strokeWidth={1.75} className="text-cf-ink-subtle" aria-hidden />
        <h2 className="font-mono text-[10px] tracking-[0.14em] text-cf-ink-subtle uppercase">
          {t('admin.minimumBid.title')}
        </h2>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3.5">
        <div className="min-w-0">
          <dt className="font-mono text-[10px] tracking-[0.1em] text-cf-ink-subtle uppercase">
            {t('admin.minimumBid.currentPrice')}
          </dt>
          <dd className="mt-1 font-mono text-sm font-medium">
            {stateQuery.isLoading
              ? t('common.loading.default')
              : formatChf(current?.minimumBidPrice)}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="font-mono text-[10px] tracking-[0.1em] text-cf-ink-subtle uppercase">
            {t('admin.minimumBid.source')}
          </dt>
          <dd className="mt-1 truncate text-sm text-cf-ink-muted">
            {current ? t(`admin.minimumBid.sources.${current.source}`) : '—'}
          </dd>
        </div>
      </dl>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="minimum-bid-input">
          {t('admin.minimumBid.newPrice')}
        </label>
        <input
          id="minimum-bid-input"
          type="number"
          min={0}
          step={100}
          inputMode="numeric"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t('admin.minimumBid.newPrice')}
          className="h-10 w-40 rounded-cf-md border border-cf-line bg-cf-surface px-3 font-mono text-sm outline-none focus:border-cf-ink"
        />
        <Button onClick={requestChange} disabled={mutation.isPending || !draftIsValid}>
          {t('admin.minimumBid.setPrice')}
        </Button>
      </div>

      <ConfirmDialog
        open={confirming}
        title={t('admin.minimumBid.confirmTitle')}
        tone="danger"
        confirmLabel={t('admin.minimumBid.confirm')}
        busy={mutation.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() => void applyChange()}
        message={
          <>
            <p>{t('admin.minimumBid.confirmMessage', { price: formatChf(parsed) })}</p>
            <p className="mt-2">
              <strong>{t('admin.minimumBid.warningTitle')}:</strong>{' '}
              {t('admin.minimumBid.warningMessageAny')}
            </p>
          </>
        }
      />
    </section>
  );
}
